import {
  fetchDashboardExperienceV2,
  type DashboardExperienceResponse,
} from '@library/api-dashboard-experience'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { useEffect, useRef } from 'react'
import useSWR from 'swr'

import { DASHBOARD_EXPERIENCE_CACHE_KEY } from '../configs'
import { DASHBOARD_EXPERIENCE_TELEMETRY_EVENTS } from './dashboardExperienceTelemetry'

type UseDashboardExperienceResult = {
  order_tabs: DashboardExperienceResponse['order_tabs']
  orders: NonNullable<DashboardExperienceResponse['orders']>
  sections?: DashboardExperienceResponse['sections']
  error: Error | undefined
  isLoading: boolean
  isSessionExpired?: boolean
}

const initialState: UseDashboardExperienceResult = {
  order_tabs: [],
  orders: [],
  error: undefined,
  isLoading: true,
}

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status

    if (typeof status === 'number') {
      return status
    }
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string') {
      const statusMatch = message.match(/(?:status=|status(?: code)?:\s*|with\s)(\d{3})/i)

      if (statusMatch) {
        return Number(statusMatch[1])
      }
    }
  }

  return undefined
}

const useDashboardExperience = (): UseDashboardExperienceResult => {
  const { data, error } = useSWR(DASHBOARD_EXPERIENCE_CACHE_KEY, () => fetchDashboardExperienceV2())
  const hasTrackedApiSuccess = useRef(false)

  useEffect(() => {
    if (!data || error || hasTrackedApiSuccess.current) {
      return
    }

    hasTrackedApiSuccess.current = true
    datadogRum.addAction(DASHBOARD_EXPERIENCE_TELEMETRY_EVENTS.apiSuccess, {
      page: 'MyHomePage',
      statusCode: 200,
    })
  }, [data, error])

  if (error) {
    const errorMessage = `Error fetching dashboard experience in myHome ${JSON.stringify(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(error, {
      page: 'MyHomePage',
      errorMessage: errorMessage,
    })
    if (statusCode && statusCode !== 200) {
      datadogRum.addAction('dashboard_experience_api_error', {
        page: 'MyHomePage',
        statusCode,
      })
    }

    return {
      ...initialState,
      error,
      isLoading: false,
      ...(statusCode === 401 ? { isSessionExpired: true } : {}),
    }
  }

  if (!data) {
    return initialState
  }

  return {
    order_tabs: data.order_tabs ?? initialState.order_tabs,
    orders: data.orders ?? initialState.orders,
    ...(data.sections !== undefined ? { sections: data.sections } : {}),
    error,
    isLoading: false,
  }
}

export { useDashboardExperience }
